import {
  FC,
  HTMLProps,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// libs
import classNames from "classnames";

// components
import { Error } from "@/components/common";

// assets
import { ArrowDown, MagnifyingGlass } from "@/components/assets/icons";
import { countries } from "./country-codes";
import { useTheme } from "@/context/Theme.context";
import { Theme } from "@/enums";
import Image from "next/image";
import { useTranslations } from "next-intl";

type InputProps = {
  id?: string;
  label?: string;
  name?: string;
  error?: any;
  value?: string | number;
  type?: string;
  required?: boolean;
  countriesList?: string[];
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  iconPosition?: "left" | "right";
  onChange: ({
    code,
    phoneNumber,
  }: {
    code: string;
    phoneNumber: string;
  }) => void;
} & HTMLProps<HTMLInputElement>;

const PhoneInput: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label = "",
      value,
      countriesList,
      name,
      onChange,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [selectedCountry, setSelectedCountry] = useState<{
      code: string;
      flag: string;
      name: string;
      phoneCode: string;
    }>();
    useEffect(() => {
      setPhoneNumber(
        value?.toString()?.replace(selectedCountry?.phoneCode, ""),
      );
    }, [value, selectedCountry]);

    const countryCode = useMemo(() => {
      return countries.filter((country) => {
        const countryCode = country?.phoneCode;
        if (
          phoneNumber?.toString()?.includes(countryCode) &&
          countryCode !== selectedCountry?.phoneCode
        ) {
          return country;
        }
      })?.[0]?.phoneCode;
    }, [phoneNumber, selectedCountry]);

    const wrapperRef = useRef(null);
    const [openDropdown, setOpenDropdown] = useState<boolean>(false);

    const t = useTranslations();

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpenDropdown(false);
      }
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);
    useEffect(() => {
      if (!selectedCountry) {
        if (theme === Theme.GEORGIA) {
          const georgia = countries.filter(
            (country) => country.code === "GE",
          )?.[0];
          setSelectedCountry(georgia);
        } else if (theme === Theme.UK) {
          const ukraine = countries.filter(
            (country) => country.code === "UA",
          )?.[0];
          setSelectedCountry(ukraine);
        } else if (theme === Theme.UZ) {
          const Uzbekistan = countries.filter(
            (country) => country.code === "UZ",
          )?.[0];
          setSelectedCountry(Uzbekistan);
        }
      }
    }, [theme]);

    const inputClasses = classNames(
      "w-full",
      "bg-transparent",
      "h-full",
      "pr-8",
      "leading-tight",
      "text-base",
      "font-normal",
      "break-all",
      "focus:outline-none",
      "active:outline-none",
      className,
      {
        // error
        "border-red-600": !!error,
        // active
        "border border-transparent": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
      },
    );

    return (
      <div
        className={classNames("relative w-full", containerClassName)}
        ref={wrapperRef}
      >
        <div
          className={classNames(
            "input-wrapper flex h-[62px] w-full rounded-[2.25rem] border bg-gray-100 p-[4px]",
            wrapperClassName,
            {
              "border-transparent active:border-[#cfe0e2]": !error,
              "border-red-600": !!error,
            },
          )}
        >
          <div
            className="ml-[23px] flex h-[54px] items-center gap-[4px] rounded-[27px] py-[13px]"
            onClick={() => setOpenDropdown(!openDropdown)}
          >
            <div className="inline-flex h-7 min-h-7 w-7 min-w-7 flex-col items-center justify-center overflow-hidden rounded-full">
              {selectedCountry?.flag && (
                <Image
                  src={selectedCountry?.flag}
                  alt={selectedCountry?.name}
                  width={28}
                  height={28}
                  className="transition-500 h-full object-cover transition-all"
                />
              )}
            </div>
            <ArrowDown />
          </div>

          <div className="relative flex h-full w-full flex-row-reverse items-end">
            <input
              id={id}
              ref={ref}
              className={classNames("input-field pt-4", inputClasses)}
              name={name}
              disabled={disabled}
              maxLength={maxLength}
              placeholder=""
              onChange={(e) => {
                setPhoneNumber(
                  e.target.value
                    ?.toString()
                    ?.replace(selectedCountry?.phoneCode, "")
                    ?.replace(countryCode, "") || "",
                );
                onChange({
                  code: e.target.value ? selectedCountry?.phoneCode || "" : "",
                  phoneNumber:
                    e.target.value
                      ?.toString()
                      ?.replace(selectedCountry?.phoneCode, "")
                      ?.replace(countryCode, "") || "",
                });
              }}
              value={
                phoneNumber
                  ?.toString()
                  ?.replace(selectedCountry?.phoneCode, "")
                  ?.replace(countryCode, "") || ""
              }
              type="text"
              pattern="^\+?[0-9]\d{1,14}$"
              onKeyPress={(event) => {
                const charCode = event.which ? event.which : event.keyCode;
                if (
                  !/[0-9]/.test(String.fromCharCode(charCode)) &&
                  !(event.key === "Enter" || event.keyCode === 13)
                ) {
                  event.preventDefault();
                }
              }}
              onInput={(event) => {
                const input = event.target as HTMLInputElement;
                const value = input.value;
                if (
                  value.length === 1 &&
                  value !== "+" &&
                  !/^\d$/.test(value)
                ) {
                  input.value = "";
                }
                input.value = value.replace(/[^0-9]/g, "");
              }}
              {...props}
            />
            <p className="country-code ml-[13px] mr-[7px] h-full pt-[24px] text-base font-normal text-neutral-400">
              ({selectedCountry?.phoneCode})
            </p>

            <label
              className={classNames(
                "input-label !left-[13px] leading-tight",
                labelClassName,
                !!error && "text-red-600 !opacity-100",
              )}
              htmlFor={id}
            >
              {t(label)}
            </label>
          </div>
          <CountrySelect
            selectedCountry={selectedCountry}
            onChange={(country) => {
              setSelectedCountry(country);
              onChange({
                code: country?.phoneCode,
                phoneNumber: phoneNumber,
              });
              setOpenDropdown(false);
            }}
            isOpen={openDropdown}
            countriesList={countriesList}
          />
        </div>
        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };

const CountrySelect = ({
  selectedCountry,
  onChange,
  isOpen,
  countriesList,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const themeCountries = useMemo(() => {
    switch (theme) {
      case Theme.UZ:
        return countries?.filter((country) => {
          if (countriesList) {
            return countriesList.includes(country.code);
          } else {
            return country.code === "UZ";
          }
        });
      case Theme.GEORGIA:
        return countries?.filter((country) => {
          if (countriesList) {
            return countriesList.includes(country.code);
          } else {
            return country.code === "GE";
          }
        });
      default:
        return countries;
    }
  }, [theme]);
  const [filteredCountries, setFilteredCountries] = useState(themeCountries);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setFilteredCountries(
      themeCountries.filter(
        (country) =>
          country.code.toLowerCase().includes(query.toLowerCase()) ||
          country.name.toLowerCase().includes(query.toLowerCase()) ||
          country.phoneCode.includes(query),
      ),
    );
  };
  return (
    <div
      className={classNames(
        "absolute left-[-1px] top-[72px] z-10 w-[calc(100%+2px)] overflow-y-scroll rounded-[20px] bg-white shadow transition-all duration-300",
        {
          "opacity-1 max-h-[213px]": isOpen,
          "h-0 opacity-0": !isOpen,
        },
      )}
    >
      <div className="mx-[10px] mt-[10px] flex items-center gap-[6px] border-b-[1px] border-zinc-100 pb-[7px]">
        <div className="flex h-[24px] w-[24px] items-center justify-center">
          <MagnifyingGlass />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Search for a country"
          className="w-full text-xs font-medium placeholder:text-xs placeholder:text-[#00000080] focus:outline-none active:outline-none"
        />
      </div>
      <ul>
        {filteredCountries?.map((country, i) => {
          return (
            <li key={i}>
              <button
                onClick={() => onChange(country)}
                className="flex items-center p-[10px]"
                type="button"
              >
                <div className="flex h-[40px] w-[40px] items-center justify-center overflow-hidden rounded-full">
                  <Image
                    src={country.flag}
                    alt={country.name}
                    width={40}
                    height={40}
                    className="h-full object-cover"
                  />
                </div>
                <h2 className="ml-[10px] mr-[3px] text-xs font-medium text-black">
                  {country.name}
                </h2>
                <span className="text-xs font-medium text-neutral-400">
                  ({country.phoneCode})
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
